import { LinkIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { usePropChange } from '@netiva/classifieds-common';
import { FC, useCallback, useState } from 'react';
import { useSelectedLinkNode } from '../../hooks';
import { ToolbarButton, ToolbarButtonProps } from './ToolbarButton';
import { useTranslation } from 'react-i18next';

export type LinkButtonProps = Omit<ToolbarButtonProps, 'children'>;

export const LinkButton: FC<LinkButtonProps> = ({ ...rest }) => {
  const { t } = useTranslation('html-editor');
  const [editor] = useLexicalComposerContext();
  const [url, setUrl] = useState('');

  const linkNode = useSelectedLinkNode();
  const isActive = linkNode !== null;

  if (usePropChange(linkNode)) {
    if (linkNode) {
      editor.read(() => {
        setUrl(linkNode.getURL() || '');
      });
    } else {
      setUrl('');
    }
  }

  const { isOpen, onToggle, onClose } = useDisclosure();

  const handleRemove = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  }, [editor]);

  const handleApply = useCallback(() => {
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url,
        target: '_blank',
        rel: 'nofollow noopener noreferrer',
      });
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, url]);

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom">
      <PopoverTrigger>
        <ToolbarButton isActive={isActive} onClick={onToggle} title={t('htmlEditor.toolbar.link')} {...rest}>
          <LinkIcon />
        </ToolbarButton>
      </PopoverTrigger>
      {/* in a portal so it doesn't interfere with form validation when included in a form (i.e. in the admin) */}
      <Portal>
        <PopoverContent>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader>{t('htmlEditor.linkPopup.title')}</PopoverHeader>
          <PopoverBody>
            <VStack alignItems="stretch" spacing={2}>
              <FormControl isRequired>
                <FormLabel size="sm">{t('htmlEditor.linkPopup.url')}</FormLabel>
                <Input type="url" size="sm" value={url} onChange={(e) => setUrl(e.currentTarget.value)} />
              </FormControl>
              <Flex justify="space-between">
                <Button type="button" colorScheme="red" onClick={handleRemove} isDisabled={!isActive}>
                  {t('htmlEditor.linkPopup.remove')}
                </Button>
                <Button type="button" colorScheme="primary" onClick={handleApply}>
                  {t('htmlEditor.linkPopup.apply')}
                </Button>
              </Flex>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
